import { useCallback, useState } from "react";

import { useAuth, type Profile } from "@/contexts/AuthContext";
import { MARKETING_CONSENT_TEXT_VERSION } from "@/config/consent";
import { fetchClientIp } from "@/lib/fetchClientIp";

/**
 * Hook backing the Profile-page marketing opt-in nudge.
 *
 * Intentionally one-way: there is no `optOut`. Users who change their mind
 * unsubscribe via the link in the marketing email itself, not from the profile
 * UI. See `feedback-marketing-consent-opt-in-only-profile` in memory.
 *
 * When the user opts in we stamp the full audit trail (consent boolean +
 * timestamp + IP + text version) so the consent event is later defensible
 * under GDPR Art. 7(1) and IL Communications Law §30A.
 */
export function useMarketingOptIn() {
  const { profile, updateProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const isOptedIn = profile?.marketing_consent === true;

  const optIn = useCallback(async () => {
    if (isOptedIn || isSaving) return;
    setIsSaving(true);
    const updates: Partial<Profile> = {
      marketing_consent: true,
      marketing_consent_at: new Date().toISOString(),
      marketing_consent_text_version: MARKETING_CONSENT_TEXT_VERSION,
      marketing_consent_ip: await fetchClientIp(),
    };
    await updateProfile(updates);
    setIsSaving(false);
  }, [isOptedIn, isSaving, updateProfile]);

  return { isOptedIn, isSaving, optIn };
}
